import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getTenderProcessList } from "../../../../services/AppService";
import { formatCurrency } from "../../../../utility/formatCurrency";
import { useTranslation } from "react-i18next";

const AddNegotiationForm = ({ onSubmit, editData, onClose, open }) => {
    const {t}=useTranslation();
    const [formData, setFormData] = useState({
        tenderProcessId: "",
        bidderName: "",
        aboveBelow: "",
        bidAmount: ""
    });

    // Display-only state for auto-filled fields
    const [tenderDisplay, setTenderDisplay] = useState({
        workTitle: "",
        tenderDate: "",
        tenderValue: ""
    });

    const [tenderList, setTenderList] = useState([]);

    useEffect(() => {
        if (open) {
            fetchTenders();
        }
    }, [open]);

    const fetchTenders = async () => {
        try {
            // Fetching tenders to populate the dropdown
            const res = await getTenderProcessList("", 0, 1000); 
            setTenderList(res.content || []);
        } catch (err) {
            console.error("Failed to fetch tenders", err);
        }
    };

    // Auto-fill logic when Tender ID changes
    useEffect(() => {
        if (formData.tenderProcessId) {
            const selected = tenderList.find(t => t.id === Number(formData.tenderProcessId));
            if (selected) {
                setTenderDisplay({
                    workTitle: selected.workTypeName || "",
                    tenderDate: selected.date || "",
                    tenderValue: selected.tenderValue || ""
                });
            }
        } else {
            setTenderDisplay({ workTitle: "", tenderDate: "", tenderValue: "" });
        }
    }, [formData.tenderProcessId, tenderList]);

    useEffect(() => {
        if (editData && open) {
            setFormData({
                tenderProcessId: editData.tenderProcessId || "",
                bidderName: editData.bidderName || "",
                aboveBelow: editData.aboveBelow || "",
                bidAmount: editData.bidAmount || ""
            });
        } else {
            resetForm();
        }
    }, [editData, open]);

    const resetForm = () => {
        setFormData({ tenderProcessId: "", bidderName: "", aboveBelow: "", bidAmount: "" });
        setTenderDisplay({ workTitle: "", tenderDate: "", tenderValue: "" });
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            tenderProcessId: Number(formData.tenderProcessId),
            aboveBelow: Number(formData.aboveBelow),
            bidAmount: Number(formData.bidAmount)
        };
        onSubmit(payload);
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{t("pageTitles.negotiationProcessList")}</div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>
                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        {/* Section 1: Search Tender (Auto-filled) */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.negotiationProcess.searchTender")}:</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("tables.negotiationProcess.tenderId")} <span>*</span></label>
                                    <select 
                                        name="tenderProcessId" 
                                        value={formData.tenderProcessId} 
                                        onChange={handleFieldChange} 
                                        className="staff-modal-input" 
                                        required
                                    >
                                        <option value="">Select Tender Id</option>
                                        {tenderList.map(t => (
                                            <option key={t.id} value={t.id}>{t.tenderId}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.tenderDate")}</label>
                                    <input value={tenderDisplay.tenderDate || ""} className="staff-modal-input bg-gray-100" readOnly placeholder="Tender Date" />
                                </div>
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.workTitle")}</label>
                                    <input value={tenderDisplay.workTitle || ""} className="staff-modal-input bg-gray-100" readOnly placeholder="Work Title" />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.tenderValue")}</label>
                                    <input value={formatCurrency(tenderDisplay.tenderValue || "")} className="staff-modal-input bg-gray-100" readOnly placeholder="Tender Value" />
                                </div>
                            </div>
                        </fieldset>

                        {/* Section 2: Contractor Details */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.negotiationProcess.contractorDetails")}</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.bidderName")} <span>*</span></label>
                                    <input 
                                        name="bidderName" 
                                        placeholder="Enter Bidder Name" 
                                        value={formData.bidderName} 
                                        onChange={handleFieldChange} 
                                        className="staff-modal-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.aboveBelow")}<span>*</span></label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        name="aboveBelow" 
                                        placeholder="Enter Above / Below %" 
                                        value={formData.aboveBelow} 
                                        onChange={handleFieldChange} 
                                        className="staff-modal-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.negotiationProcess.bidAmount")} <span>*</span></label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        name="bidAmount" 
                                        placeholder="0.00" 
                                        value={formData.bidAmount} 
                                        onChange={handleFieldChange} 
                                        className="staff-modal-input" 
                                        required 
                                    />
                                </div>
                            </div>
                        </fieldset>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12 uppercase">{t('common.save')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddNegotiationForm;